import React, { useState, useEffect, useRef } from 'react';
import { FileText, Plus, User, Home, ExternalLink, Image, X, RefreshCw, ChevronDown, Zap } from 'lucide-react';
import DatePicker from './DatePicker';
import CustomSelect from './common/CustomSelect';
import AccountSelect from './common/AccountSelect';
import { PaymentAccountModal } from './modals';
import { paymentAccountAPI } from '../services/api';
import useLogoFetch from '../hooks/useLogoFetch';
import '../styles/Wizard.css';

// Parse billing frequency into components
function parseFrequency(freq) {
  if (!freq) return { interval: 1, unit: 'monthly' };
  
  // Handle custom frequencies like "6-weekly" or "3-monthly"
  const customMatch = freq.match(/^(\d+)-(\w+)$/);
  if (customMatch) {
    return { interval: parseInt(customMatch[1]), unit: customMatch[2] };
  }
  
  // Map standard frequencies
  const standardMap = {
    'weekly': { interval: 1, unit: 'weekly' },
    'biweekly': { interval: 2, unit: 'weekly' },
    'monthly': { interval: 1, unit: 'monthly' },
    'quarterly': { interval: 3, unit: 'monthly' },
    'annually': { interval: 1, unit: 'yearly' },
    'one-time': { interval: 0, unit: 'one-time' }
  };
  
  return standardMap[freq] || { interval: 1, unit: 'monthly' };
}

// Build billing frequency string from components
function buildFrequency(interval, unit) {
  if (unit === 'one-time') return 'one-time';
  
  // Map back to standard frequencies where possible
  if (interval === 1 && unit === 'weekly') return 'weekly';
  if (interval === 2 && unit === 'weekly') return 'biweekly';
  if (interval === 1 && unit === 'monthly') return 'monthly';
  if (interval === 3 && unit === 'monthly') return 'quarterly';
  if (interval === 1 && unit === 'yearly') return 'annually';
  
  // Custom frequency
  return `${interval}-${unit}`;
}

// Domain mapping for common bill providers
const BILL_DOMAIN_MAP = {
  // Utilities
  'at&t': 'att.com',
  'att': 'att.com',
  'verizon': 'verizon.com',
  't-mobile': 't-mobile.com',
  'tmobile': 't-mobile.com',
  'sprint': 'sprint.com',
  'xfinity': 'xfinity.com',
  'comcast': 'xfinity.com',
  'spectrum': 'spectrum.com',
  'cox': 'cox.com',
  'centurylink': 'centurylink.com',
  'frontier': 'frontier.com',
  'optimum': 'optimum.com',
  'google fiber': 'fiber.google.com',
  
  // Electric/Gas
  'duke energy': 'duke-energy.com',
  'pge': 'pge.com',
  'pg&e': 'pge.com',
  'con edison': 'coned.com',
  'coned': 'coned.com',
  'national grid': 'nationalgridus.com',
  'dominion': 'dominionenergy.com',
  'entergy': 'entergy.com',
  'xcel': 'xcelenergy.com',
  'dte': 'dteenergy.com',
  'consumers energy': 'consumersenergy.com',
  'oncor': 'oncor.com',
  'txu': 'txu.com',
  'reliant': 'reliant.com',
  
  // Water
  'american water': 'amwater.com',
  
  // Streaming
  'netflix': 'netflix.com',
  'hulu': 'hulu.com',
  'disney': 'disneyplus.com',
  'disney+': 'disneyplus.com',
  'hbo': 'hbomax.com',
  'hbo max': 'hbomax.com',
  'max': 'max.com',
  'amazon prime': 'amazon.com',
  'prime video': 'amazon.com',
  'apple tv': 'apple.com',
  'apple music': 'apple.com',
  'spotify': 'spotify.com',
  'youtube': 'youtube.com',
  'youtube tv': 'youtube.com',
  'peacock': 'peacocktv.com',
  'paramount': 'paramountplus.com',
  'paramount+': 'paramountplus.com',
  'crunchyroll': 'crunchyroll.com',
  'audible': 'audible.com',
  
  // Insurance
  'geico': 'geico.com',
  'state farm': 'statefarm.com',
  'allstate': 'allstate.com',
  'progressive': 'progressive.com',
  'liberty mutual': 'libertymutual.com',
  'farmers': 'farmers.com',
  'usaa': 'usaa.com',
  'nationwide': 'nationwide.com',
  'travelers': 'travelers.com',
  'aetna': 'aetna.com',
  'cigna': 'cigna.com',
  'united healthcare': 'uhc.com',
  'blue cross': 'bcbs.com',
  'kaiser': 'kaiserpermanente.org',
  'humana': 'humana.com',
  'anthem': 'anthem.com',
  'metlife': 'metlife.com',
  
  // Credit Cards / Banks
  'chase': 'chase.com',
  'bank of america': 'bankofamerica.com',
  'bofa': 'bankofamerica.com',
  'wells fargo': 'wellsfargo.com',
  'citi': 'citi.com',
  'citibank': 'citi.com',
  'capital one': 'capitalone.com',
  'discover': 'discover.com',
  'american express': 'americanexpress.com',
  'amex': 'americanexpress.com',
  'us bank': 'usbank.com',
  'pnc': 'pnc.com',
  'td bank': 'td.com',
  'navy federal': 'navyfederal.org',
  'ally': 'ally.com',
  'synchrony': 'synchrony.com',
  'barclays': 'barclays.com',
  'apple card': 'apple.com',
  'paypal': 'paypal.com',
  'venmo': 'venmo.com',
  'marcus': 'marcus.com',
  'sofi': 'sofi.com',
  
  // Loans
  'sallie mae': 'salliemae.com',
  'navient': 'navient.com',
  'nelnet': 'nelnet.com',
  'great lakes': 'mygreatlakes.org',
  'fedloan': 'myfedloan.org',
  'mohela': 'mohela.com',
  
  // Subscriptions/Software
  'adobe': 'adobe.com',
  'microsoft': 'microsoft.com',
  'office 365': 'microsoft.com',
  'dropbox': 'dropbox.com',
  'google': 'google.com',
  'icloud': 'apple.com',
  'slack': 'slack.com',
  'zoom': 'zoom.us',
  'github': 'github.com',
  'notion': 'notion.so',
  'canva': 'canva.com',
  '1password': '1password.com',
  'lastpass': 'lastpass.com',
  'nordvpn': 'nordvpn.com',
  'expressvpn': 'expressvpn.com',
  
  // Fitness/Health
  'planet fitness': 'planetfitness.com',
  'la fitness': 'lafitness.com',
  '24 hour fitness': '24hourfitness.com',
  'anytime fitness': 'anytimefitness.com',
  'peloton': 'onepeloton.com',
  'orange theory': 'orangetheory.com',
  'ymca': 'ymca.org',
  'headspace': 'headspace.com',
  'calm': 'calm.com',
  
  // Retail/Memberships
  'amazon': 'amazon.com',
  'costco': 'costco.com',
  'sams club': 'samsclub.com',
  'walmart': 'walmart.com',
  'target': 'target.com',
  'best buy': 'bestbuy.com',
  'home depot': 'homedepot.com',
  'lowes': 'lowes.com',
  'aaa': 'aaa.com',
  
  // News/Media
  'new york times': 'nytimes.com',
  'nyt': 'nytimes.com',
  'wall street journal': 'wsj.com',
  'wsj': 'wsj.com',
  'washington post': 'washingtonpost.com',
  'the athletic': 'theathletic.com',
  'medium': 'medium.com',
  'substack': 'substack.com',
  
  // Gaming
  'xbox': 'xbox.com',
  'playstation': 'playstation.com',
  'psn': 'playstation.com',
  'nintendo': 'nintendo.com',
  'steam': 'steampowered.com',
  'ea': 'ea.com',
  'epic games': 'epicgames.com',
  
  // Food Delivery
  'doordash': 'doordash.com',
  'uber eats': 'ubereats.com',
  'grubhub': 'grubhub.com',
  'instacart': 'instacart.com',
  'hello fresh': 'hellofresh.com',
  'blue apron': 'blueapron.com',
  
  // Transportation
  'uber': 'uber.com',
  'lyft': 'lyft.com',
  
  // Education
  'coursera': 'coursera.org',
  'udemy': 'udemy.com',
  'skillshare': 'skillshare.com',
  'masterclass': 'masterclass.com',
  'linkedin learning': 'linkedin.com',
  'duolingo': 'duolingo.com',
};

// Cleanup regex for bill-specific noise words when guessing domains
const BILL_CLEANUP_REGEX = /\s*(bill|payment|monthly|subscription|service|account|electric|gas|water|internet|phone|mobile|wireless|tv|insurance|premium)\s*/gi;
const BILL_LOGO_OPTIONS = { domainMap: BILL_DOMAIN_MAP, cleanupRegex: BILL_CLEANUP_REGEX };

// Step 1: Details (Name, Amount, Category, Schedule)
function DetailsStep({ formData, setFormData, saving, isInHousehold, categories, logoState }) {
  const { 
    logoUrl, 
    logoOptions, 
    loading: logoLoading, 
    error: logoError, 
    retry: retryLogo, 
    setLogoUrl,
    showPicker,
    setShowPicker,
    selectOption 
  } = logoState;
  const fileInputRef = useRef(null);
  const pickerRef = useRef(null);

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    if (!newStartDate) {
      setFormData({ ...formData, startDate: '' });
      return;
    }
    const [year, month, day] = newStartDate.split('-').map(num => parseInt(num, 10));
    const date = new Date(year, month - 1, day);
    setFormData({
      ...formData,
      startDate: newStartDate,
      repeatDayOfWeek: date.getDay().toString(),
      repeatWeekOfMonth: Math.ceil(day / 7).toString(),
      dueDay: day.toString()
    });
  };

  const handleManualUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2000000) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoUrl(reader.result);
      setFormData(prev => ({ ...prev, iconUrl: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoUrl(null);
    setFormData(prev => ({ ...prev, iconUrl: '' }));
  };

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker, setShowPicker]);

  // Sync logo URL to form data when it changes
  useEffect(() => {
    if (logoUrl && !formData.iconUrl) {
      setFormData(prev => ({ ...prev, iconUrl: logoUrl }));
    }
  }, [logoUrl, formData.iconUrl, setFormData]);

  return (
    <div className="wizard-step-content">
      <h3>Bill Details</h3>
      <p className="step-description">Enter the basic information</p>

      {/* Personal vs Household Toggle */}
      {isInHousehold && (
        <div className="wizard-field">
          <label>Bill Type</label>
          <div className="ownership-toggle">
            <button
              type="button"
              className={`ownership-option ${!formData.isHouseholdBill ? 'active' : ''}`}
              onClick={() => setFormData({ ...formData, isHouseholdBill: false })}
              disabled={saving}
            >
              <User size={18} />
              <div>
                <span className="ownership-label">Personal</span>
                <span className="ownership-desc">Only you can see this bill</span>
              </div>
            </button>
            <button
              type="button"
              className={`ownership-option ${formData.isHouseholdBill ? 'active' : ''}`}
              onClick={() => setFormData({ ...formData, isHouseholdBill: true })}
              disabled={saving}
            >
              <Home size={18} />
              <div>
                <span className="ownership-label">Household</span>
                <span className="ownership-desc">Shared with all members</span>
              </div>
            </button>
          </div>
        </div>
      )}

      <div className="wizard-field">
        <label>Bill Name</label>
        <div className="bill-name-row">
          {/* Logo Preview */}
          <div className="bill-logo-preview" ref={pickerRef}>
            {logoLoading ? (
              <div className="logo-loading">
                <RefreshCw size={16} className="spinning" />
              </div>
            ) : logoUrl || formData.iconUrl ? (
              <div className="logo-thumbnail-wrapper">
                <div 
                  className={`logo-thumbnail ${logoOptions.length > 1 ? 'has-options' : ''}`}
                  onClick={() => logoOptions.length > 1 && setShowPicker(!showPicker)}
                  title={logoOptions.length > 1 ? 'Click to choose a different logo' : ''}
                >
                  <img src={logoUrl || formData.iconUrl} alt="" />
                  {logoOptions.length > 1 && (
                    <div className="logo-options-indicator">
                      <ChevronDown size={12} />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="logo-remove-btn"
                  onClick={handleRemoveLogo}
                  title="Remove logo"
                >
                  <X size={12} />
                </button>
                
                {/* Logo Options Picker */}
                {showPicker && logoOptions.length > 1 && (
                  <div className="logo-picker-dropdown">
                    <div className="logo-picker-header">Choose a logo</div>
                    <div className="logo-picker-grid">
                      {logoOptions.map((option, index) => (
                        <button
                          key={option.domain || index}
                          type="button"
                          className={`logo-picker-option ${option.dataUrl === logoUrl ? 'selected' : ''}`}
                          onClick={() => {
                            selectOption(option);
                            setFormData(prev => ({ ...prev, iconUrl: option.dataUrl }));
                          }}
                          title={option.name || option.domain}
                        >
                          <img src={option.dataUrl} alt={option.name || option.domain} />
                          <span className="logo-picker-label">{option.name || option.domain}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="logo-picker-upload"
                      onClick={() => {
                        setShowPicker(false);
                        fileInputRef.current?.click();
                      }}
                    >
                      <Image size={14} />
                      <span>Upload custom</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="logo-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Upload logo"
              >
                <Image size={24} />
                <span>Upload</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleManualUpload}
              style={{ display: 'none' }}
            />
          </div>
          
          {/* Name Input */}
          <div className="bill-name-input-wrapper">
            <input
              type="text"
              name="billName"
              value={formData.billName}
              onChange={(e) => setFormData({ ...formData, billName: e.target.value, iconUrl: '' })}
              disabled={saving}
              placeholder="e.g., Netflix, AT&T, Electric Bill"
              autoFocus
            />
            {!formData.billName && <span className="required-indicator">*</span>}
          </div>
        </div>
        {logoError && !logoUrl && formData.billName && (
          <p className="field-hint logo-hint">
            <span>Couldn't find logo automatically.</span>
            <button type="button" className="retry-link" onClick={retryLogo}>Retry</button>
            <span> or </span>
            <button type="button" className="retry-link" onClick={() => fileInputRef.current?.click()}>upload manually</button>
          </p>
        )}
      </div>

      <div className="wizard-row">
        <div className="wizard-field">
          <label>Amount</label>
          <div className="currency-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              step="0.01"
              min="0"
              disabled={saving}
              placeholder="0.00"
              className="currency-input"
            />
          </div>
        </div>
        <div className="wizard-field">
          <label>Category</label>
          <CustomSelect
            name="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            disabled={saving}
            placeholder="Select..."
            options={categories.map(cat => ({
              value: cat.name,
              label: cat.name,
              icon: cat.icon
            }))}
          />
        </div>
      </div>

      <div className="wizard-field">
        <label>Due Date</label>
        <DatePicker
          name="startDate"
          value={formData.startDate}
          onChange={handleStartDateChange}
          placeholder="Select date"
          disabled={saving}
        />
      </div>

      <div className="wizard-field">
        <label>Frequency</label>
        {(() => {
          const { interval, unit } = parseFrequency(formData.billingFrequency);
          // Determine if this is a custom frequency (not matching any preset)
          const isCustom = !['one-time', 'weekly', 'monthly', 'annually'].includes(formData.billingFrequency);
          
          // Determine the display value for the main dropdown
          const selectValue = isCustom ? 'custom' : formData.billingFrequency;
          
          return (
            <>
              <select
                value={selectValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    // Switch to custom mode - default to every 2 months
                    setFormData({ ...formData, billingFrequency: '2-monthly' });
                  } else {
                    setFormData({ ...formData, billingFrequency: value });
                  }
                }}
                disabled={saving}
              >
                <option value="one-time">One-time</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="annually">Yearly</option>
                <option value="custom">Custom...</option>
              </select>
              {isCustom && (
                <div className="custom-frequency">
                  <span className="custom-frequency-label">Repeat every</span>
                  <select
                    className="custom-frequency-interval"
                    value={interval}
                    onChange={(e) => {
                      const newInterval = parseInt(e.target.value);
                      setFormData({ ...formData, billingFrequency: buildFrequency(newInterval, unit) });
                    }}
                    disabled={saving}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <select
                    className="custom-frequency-unit"
                    value={unit}
                    onChange={(e) => {
                      setFormData({ ...formData, billingFrequency: buildFrequency(interval, e.target.value) });
                    }}
                    disabled={saving}
                  >
                    <option value="weekly">weeks</option>
                    <option value="monthly">months</option>
                    <option value="yearly">years</option>
                  </select>
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="wizard-field">
        <label>Expiration Date <span className="optional-label">(optional)</span></label>
        <DatePicker
          name="endDate"
          value={formData.endDate || ''}
          onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
          placeholder="No expiration"
          disabled={saving}
        />
      </div>

      <div className="wizard-field">
        <label className="toggle-field">
          <input
            type="checkbox"
            checked={formData.isVariableAmount}
            onChange={(e) => setFormData({ ...formData, isVariableAmount: e.target.checked })}
            disabled={saving}
          />
          <span>Variable amount (changes each cycle)</span>
        </label>
      </div>
    </div>
  );
}

// Step 2: Payment & Notes
function PaymentStep({ formData, setFormData, paymentAccounts, setPaymentAccounts, members, saving, currentUser, isPrivateMode, isInHousehold }) {
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  const filteredPaymentAccounts = paymentAccounts.filter(account => {
    if (formData.isHouseholdBill) return !!account.household_id;
    return !account.household_id;
  });

  useEffect(() => {
    const isPersonalBill = !formData.isHouseholdBill;
    if (isPersonalBill && currentUser && !formData.assignedTo) {
      setFormData(prev => ({ ...prev, assignedTo: currentUser.id.toString() }));
    }
  }, [formData.isHouseholdBill, currentUser, formData.assignedTo, setFormData]);

  useEffect(() => {
    if (formData.paymentAccountId) {
      const selectedAccount = paymentAccounts.find(a => a.id.toString() === formData.paymentAccountId);
      if (selectedAccount) {
        const isValidForBillType = formData.isHouseholdBill ? !!selectedAccount.household_id : !selectedAccount.household_id;
        if (!isValidForBillType) {
          setFormData(prev => ({ ...prev, paymentAccountId: '' }));
        }
      }
    }
  }, [formData.isHouseholdBill, formData.paymentAccountId, paymentAccounts, setFormData]);

  const handleAccountCreated = async (accountData) => {
    setCreatingAccount(true);
    try {
      const response = await paymentAccountAPI.create(accountData);
      const newAccount = response.data;
      setPaymentAccounts(prev => [...prev, newAccount]);
      setFormData(prev => ({ ...prev, paymentAccountId: newAccount.id.toString() }));
      setShowQuickAdd(false);
    } catch (err) {
      throw err;
    } finally {
      setCreatingAccount(false);
    }
  };

  const isValidUrl = (url) => {
    if (!url) return true;
    try { new URL(url); return true; } catch { return false; }
  };

  return (
    <div className="wizard-step-content">
      <h3>Payment & Notes</h3>
      <p className="step-description">How will this bill be paid?</p>

      <div className="wizard-field">
        <label>Payment Account</label>
        <div className="payment-account-row">
          <AccountSelect
            accounts={filteredPaymentAccounts}
            value={formData.paymentAccountId}
            onChange={(e) => setFormData({ ...formData, paymentAccountId: e.target.value })}
            disabled={saving}
            placeholder="Select account..."
            showOwnership={isInHousehold}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowQuickAdd(true)}
            disabled={saving}
            title="Add new payment account"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      <div className="wizard-field">
        <label>Assigned To</label>
        {isPrivateMode || !formData.isHouseholdBill ? (
          <input
            type="text"
            value={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'You'}
            disabled
            style={{ backgroundColor: 'var(--background-secondary)', cursor: 'not-allowed' }}
          />
        ) : (
          <CustomSelect
            name="assignedTo"
            value={formData.assignedTo}
            onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
            disabled={saving}
            placeholder="Select person..."
            options={members.map(member => ({
              value: member.id.toString(),
              label: `${member.first_name} ${member.last_name}`
            }))}
          />
        )}
      </div>

      <div className="wizard-field">
        <label className="toggle-field">
          <input
            type="checkbox"
            name="autoPay"
            checked={formData.autoPay}
            onChange={(e) => setFormData({ ...formData, autoPay: e.target.checked })}
            disabled={saving}
          />
          <span>Auto-Pay enabled</span>
        </label>
      </div>

      <div className="wizard-field">
        <label>Website URL</label>
        <div className="url-input-wrapper">
          <input
            type="url"
            name="url"
            value={formData.url || ''}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            disabled={saving}
            placeholder="https://example.com/pay"
            className={formData.url && !isValidUrl(formData.url) ? 'input-error' : ''}
          />
          {formData.url && isValidUrl(formData.url) && (
            <a href={formData.url} target="_blank" rel="noopener noreferrer" className="url-test-link" title="Open link">
              <ExternalLink size={18} />
            </a>
          )}
        </div>
      </div>

      <div className="wizard-field">
        <label>Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          disabled={saving}
          rows={3}
          placeholder="Account number, contact info, etc."
        />
      </div>

      <PaymentAccountModal
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onSubmit={handleAccountCreated}
        saving={creatingAccount}
        forceOwnership={formData.isHouseholdBill ? 'household' : 'personal'}
      />
    </div>
  );
}

// Step 3: Review
function ReviewStep({ formData, setCurrentStep, paymentAccounts, members, currentUser, isPrivateMode, setEditingFromReview }) {
  const getFrequencyLabel = (freq) => {
    // Handle standard frequencies
    const labels = {
      'one-time': 'One-time',
      'weekly': 'Weekly',
      'biweekly': 'Every 2 weeks',
      'monthly': 'Monthly',
      'quarterly': 'Every 3 months',
      'annually': 'Yearly'
    };
    if (labels[freq]) return labels[freq];
    
    // Handle custom frequencies like "6-weekly" or "3-monthly"
    const { interval, unit } = parseFrequency(freq);
    const unitLabels = {
      'weekly': interval === 1 ? 'week' : 'weeks',
      'monthly': interval === 1 ? 'month' : 'months',
      'yearly': interval === 1 ? 'year' : 'years'
    };
    return `Every ${interval} ${unitLabels[unit] || unit}`;
  };

  const getAccountName = (id) => {
    const account = paymentAccounts.find(a => a.id === parseInt(id));
    return account ? account.account_name : 'Not set';
  };

  const getMemberName = (id) => {
    if (isPrivateMode && currentUser) {
      return `${currentUser.first_name} ${currentUser.last_name}`;
    }
    const member = members.find(m => m.id === parseInt(id));
    return member ? `${member.first_name} ${member.last_name}` : 'Unassigned';
  };

  const handleEdit = (step) => {
    setEditingFromReview(true);
    setCurrentStep(step);
  };

  return (
    <div className="wizard-step-content review-step">
      <div className="review-grid">
        <div className="review-section-compact">
          <div className="review-header-compact">
            <h4>Details</h4>
            <button type="button" className="edit-link" onClick={() => handleEdit(1)}>Edit</button>
          </div>
          <div className="review-row">
            {formData.iconUrl && <img src={formData.iconUrl} alt="" className="review-icon-small" />}
            <span className="review-label">{formData.billName || 'Untitled'}</span>
            <span className="review-value">
              {formData.isVariableAmount && '~'}${formData.amount ? parseFloat(formData.amount).toFixed(2) : '0.00'}
            </span>
          </div>
          {formData.category && <div className="review-detail">Category: {formData.category}</div>}
          <div className="review-detail">
            Due: {formData.startDate ? new Date(formData.startDate + 'T00:00:00').toLocaleDateString() : 'Not set'}
          </div>
          <div className="review-detail">Frequency: {getFrequencyLabel(formData.billingFrequency)}</div>
          {formData.endDate && (
            <div className="review-detail">Expires: {new Date(formData.endDate + 'T00:00:00').toLocaleDateString()}</div>
          )}
        </div>

        <div className="review-section-compact">
          <div className="review-header-compact">
            <h4>Payment</h4>
            <button type="button" className="edit-link" onClick={() => handleEdit(2)}>Edit</button>
          </div>
          <div className="review-detail review-detail-with-badge">
            <span>Account: {formData.paymentAccountId ? getAccountName(formData.paymentAccountId) : 'Not set'}</span>
            {formData.autoPay && <span className="review-badge autopay-badge"><Zap size={10} /> Auto-Pay</span>}
          </div>
          <div className="review-detail">
            Assigned: {formData.assignedTo ? getMemberName(formData.assignedTo) : 'Unassigned'}
          </div>
          {formData.notes && <div className="review-detail">Notes: {formData.notes}</div>}
          {formData.url && (
            <div className="review-detail review-url">
              <ExternalLink size={14} />
              <a href={formData.url} target="_blank" rel="noopener noreferrer">
                {formData.url.length > 35 ? formData.url.substring(0, 35) + '...' : formData.url}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Wizard Component
function BillWizard({ isOpen, onClose, onSubmit, bill, paymentAccounts, members, categories = [] }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [editingFromReview, setEditingFromReview] = useState(false);
  const [localPaymentAccounts, setLocalPaymentAccounts] = useState(paymentAccounts || []);
  const [formData, setFormData] = useState({
    billName: '',
    amount: '',
    isVariableAmount: false,
    billingFrequency: 'monthly',
    dueDay: '1',
    startDate: '',
    endDate: '',
    paymentAccountId: '',
    assignedTo: '',
    category: '',
    notes: '',
    url: '',
    autoPay: false,
    iconUrl: '',
    isHouseholdBill: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isPrivateMode = !currentUser.household_id;

  // Logo auto-fetch hook
  const logoState = useLogoFetch(formData.billName, BILL_LOGO_OPTIONS);

  const steps = [
    { id: 1, label: 'Details' },
    { id: 2, label: 'Payment' },
    { id: 3, label: 'Review' }
  ];

  useEffect(() => {
    setLocalPaymentAccounts(paymentAccounts || []);
  }, [paymentAccounts]);

  useEffect(() => {
    if (bill) {
      setFormData({
        billName: bill.bill_name || '',
        amount: bill.amount || '',
        isVariableAmount: bill.is_variable_amount || false,
        billingFrequency: bill.billing_frequency || 'monthly',
        dueDay: bill.due_day?.toString() || '1',
        startDate: bill.start_date || '',
        endDate: bill.end_date || '',
        paymentAccountId: bill.payment_account_id?.toString() || '',
        assignedTo: bill.assigned_to?.toString() || '',
        category: bill.category || '',
        notes: bill.notes || '',
        url: bill.url || '',
        autoPay: bill.auto_pay || false,
        iconUrl: bill.icon_url || '',
        isHouseholdBill: !!bill.household_id
      });
      // If editing, set the logo from the bill
      if (bill.icon_url) {
        logoState.setLogoUrl(bill.icon_url);
      }
      setCurrentStep(3); // Start on review for editing
    } else {
      setFormData({
        billName: '',
        amount: '',
        isVariableAmount: false,
        billingFrequency: 'monthly',
        dueDay: '1',
        startDate: '',
        endDate: '',
        paymentAccountId: '',
        assignedTo: isPrivateMode ? currentUser.id?.toString() : '',
        category: '',
        notes: '',
        url: '',
        autoPay: false,
        iconUrl: '',
        isHouseholdBill: false
      });
      logoState.clear();
      setCurrentStep(1);
    }
    setError('');
    setEditingFromReview(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill, isOpen, isPrivateMode, currentUser.id]);

  const validateCurrentStep = () => {
    if (currentStep === 1) {
      if (!formData.billName.trim()) {
        setError('Bill name is required');
        return false;
      }
      if (!formData.startDate) {
        setError('Due date is required');
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      // Sync logo URL before moving to next step
      if (logoState.logoUrl && !formData.iconUrl) {
        setFormData(prev => ({ ...prev, iconUrl: logoState.logoUrl }));
      }
      
      if (editingFromReview) {
        setEditingFromReview(false);
        setCurrentStep(3);
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (editingFromReview) {
      setEditingFromReview(false);
      setCurrentStep(3);
    } else {
      setCurrentStep(prev => prev - 1);
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    // Use logo URL from state if not already in form data
    const iconUrl = formData.iconUrl || logoState.logoUrl || null;

    try {
      await onSubmit({
        billName: formData.billName,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        isVariableAmount: formData.isVariableAmount,
        billingFrequency: formData.billingFrequency,
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        paymentAccountId: formData.paymentAccountId ? parseInt(formData.paymentAccountId) : null,
        assignedTo: formData.assignedTo ? parseInt(formData.assignedTo) : null,
        category: formData.category || null,
        notes: formData.notes || null,
        url: formData.url || null,
        autoPay: formData.autoPay,
        iconUrl: iconUrl,
        isHouseholdBill: formData.isHouseholdBill
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save bill');
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <DetailsStep
            formData={formData}
            setFormData={setFormData}
            saving={saving}
            isInHousehold={!isPrivateMode}
            categories={categories}
            logoState={logoState}
          />
        );
      case 2:
        return (
          <PaymentStep
            formData={formData}
            setFormData={setFormData}
            paymentAccounts={localPaymentAccounts}
            setPaymentAccounts={setLocalPaymentAccounts}
            members={members}
            saving={saving}
            currentUser={currentUser}
            isPrivateMode={isPrivateMode}
            isInHousehold={!isPrivateMode}
          />
        );
      case 3:
        return (
          <ReviewStep
            formData={formData}
            setCurrentStep={setCurrentStep}
            paymentAccounts={localPaymentAccounts}
            members={members}
            currentUser={currentUser}
            isPrivateMode={isPrivateMode}
            setEditingFromReview={setEditingFromReview}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container modal-wizard" onClick={(e) => e.stopPropagation()}>
        <div className="wizard-header">
          <div className="modal-title-wrapper">
            <FileText size={24} />
            <h3>{bill ? 'Edit Bill' : 'Add Bill'}</h3>
          </div>
          <button className="modal-close" onClick={onClose} disabled={saving}>×</button>
        </div>

        <div className="wizard-steps">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="step-item">
                <div className={`step-dot ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}>
                  {currentStep > step.id ? '✓' : step.id}
                </div>
                <div className="step-label">{step.label}</div>
              </div>
              {index < steps.length - 1 && (
                <div className={`step-line ${currentStep > step.id ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="wizard-body">
            {error && <div className="form-error">{error}</div>}
            {renderStep()}
          </div>

          <div className="wizard-footer">
            <div className="wizard-footer-left">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
            <div className="wizard-footer-right">
              {currentStep > 1 && currentStep < 3 && (
                <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={saving}>
                  {editingFromReview ? '← Back to Review' : '← Back'}
                </button>
              )}
              {currentStep < 3 ? (
                <button type="button" className="btn btn-primary" onClick={(e) => { e.preventDefault(); handleNext(); }} disabled={saving}>
                  {editingFromReview ? 'Done' : 'Next →'}
                </button>
              ) : (
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : bill ? 'Update Bill' : 'Save Bill'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BillWizard;
